/**
 * "No score without evidence" (Task #6807, Story #6757, Roger's request).
 *
 * A novelty or similarity score of 100 beside an EMPTY evidence list reads as "we looked and found
 * nothing", when the analysis may simply not have run (or not finished) — so a perfect score is never
 * shown while there is nothing behind it. What the screen says instead depends on what is known:
 *   - the analysis has not run / not finished  -> "Analysis not run yet"
 *   - the analysis finished and found nothing  -> "No evidence found"  (it DID run, so "not run" would be false)
 *   - the page cannot tell                     -> "No evidence yet"
 *
 * Only a perfect score is held back: a lower score with no references is a different question (and a
 * rounded 99.96 that the page would print as "100" counts as 100). Unknown evidence (the field is
 * absent, e.g. an older stored report) is NOT treated as empty — we only withhold a score when we can
 * see that the evidence list is there and has nothing in it.
 */
import type { NoveltyReport, PriorArtSummary } from '../types'

export const ANALYSIS_NOT_RUN_LABEL = 'Analysis not run yet'
export const NO_EVIDENCE_FOUND_LABEL = 'No evidence found'
export const NO_EVIDENCE_YET_LABEL = 'No evidence yet'

/** The words shown in place of a withheld score. `analysisCompleted`: true = finished, false = not run / not finished, undefined = unknown. */
export function withheldScoreText(analysisCompleted?: boolean): { label: string; hint: string } {
  if (analysisCompleted === true) {
    return {
      label: NO_EVIDENCE_FOUND_LABEL,
      hint: 'The analysis finished but found no references to back a perfect score, so no score is shown.',
    }
  }
  if (analysisCompleted === false) {
    return {
      label: ANALYSIS_NOT_RUN_LABEL,
      hint: 'A perfect score is not shown until there is evidence behind it. Run the analysis, or wait for it to finish.',
    }
  }
  return { label: NO_EVIDENCE_YET_LABEL, hint: 'A perfect score is not shown until there is evidence behind it.' }
}

/** True when `scorePct` (0-100) prints as 100 and the evidence list is known to be empty. */
export function isPerfectScoreWithoutEvidence(
  scorePct: number | null | undefined,
  evidenceCount: number | null | undefined,
): boolean {
  if (scorePct == null || evidenceCount == null) return false
  return evidenceCount === 0 && Math.round(scorePct * 10) / 10 >= 100
}

/** Matches a sentence that quotes a perfect score: "score of 100", "100/100", "100 out of 100", "scored 100". */
const PERFECT_SCORE_SENTENCE = /\bscor(?:e|ed|es|ing)\b[^.!?]{0,30}\b100\b|\b100\s*(?:\/|out of)\s*100\b/i

/**
 * Report prose is written by the analysis and may quote the score ("The novelty score of 100 reflects…").
 * When that score is withheld, the sentences that quote it are dropped so the page does not say what its
 * own score slot refuses to show. Every other sentence is kept as written.
 */
export function withoutPerfectScoreSentences(text: string): string {
  const sentences = text.match(/[^.!?]+(?:[.!?]+(?:\s+|$)|$)/g) ?? [text]
  return sentences.filter((s) => !PERFECT_SCORE_SENTENCE.test(s)).join('').trim()
}

const len = (v: unknown): number => (Array.isArray(v) ? v.length : 0)

/** Evidence behind a project / folder prior-art score: overlap areas, related papers, patent and web matches. */
export function priorArtEvidenceCount(priorArt: PriorArtSummary): number {
  return (
    len(priorArt.overlap_areas) +
    len(priorArt.related_papers) +
    len(priorArt.patent_matches) +
    len((priorArt as { web_matches?: unknown }).web_matches)
  )
}

/** Evidence behind one document's novelty report: its overlap areas plus every claim's prior art. */
export function reportEvidenceCount(report: NoveltyReport): number {
  const claims = Array.isArray(report.claim_analysis) ? report.claim_analysis : []
  return len(report.overlap_areas) + claims.reduce((sum, c) => sum + len(c.prior_art), 0)
}
